import { useMemo } from "react";
import type { AddressData } from "@vet/print-core";
import { useProfile } from "@/hooks/useProfile";
import { useClinicsForUser } from "@/hooks/useClinics";
import type { DraftContext } from "@/lib/prescriptions/editorDraft";

interface PetLike {
  name?: string | null;
  birth_date?: string | null;
  species?: { name?: string | null } | null;
  breed?: { name?: string | null } | null;
  gender?: { name?: string | null } | null;
  weights?: Array<{
    weight?: number | null;
    measured_at?: string | null;
    medical_record_id?: string | null;
    deleted_at?: string | null;
  }> | null;
  owner?: { name?: string | null; document?: string | null; addresses?: unknown[] | null } | null;
}

export function useDraftContextData({
  pet,
  medicalRecordId,
}: {
  pet: PetLike | null | undefined;
  medicalRecordId: string | null;
}): DraftContext | null {
  const { data: profile } = useProfile();
  const { data: clinics } = useClinicsForUser();

  return useMemo(() => {
    if (!profile) return null;
    const clinic = clinics?.find((c) => c.id === profile.current_clinic_id) ?? null;
    const crmv = profile.crmv_state && profile.crmv_number ? `${profile.crmv_state}${profile.crmv_number}` : "";
    return {
      pet: pet
        ? {
            name: pet.name ?? "",
            birth_date: pet.birth_date ?? null,
            species: pet.species?.name ?? null,
            breed: pet.breed?.name ?? null,
            gender: pet.gender?.name ?? null,
            weights: (pet.weights ?? []).map((w) => ({
              weight: w.weight ?? null,
              measured_at: w.measured_at ?? null,
              medical_record_id: w.medical_record_id ?? null,
              deleted_at: w.deleted_at ?? null,
            })),
            owner: pet.owner
              ? {
                  name: pet.owner.name ?? null,
                  document: pet.owner.document ?? null,
                  addresses: (pet.owner.addresses ?? []) as AddressData[],
                }
              : null,
          }
        : null,
      vet: {
        name: profile.full_name,
        crmvForPrint: crmv,
        phone: profile.phone,
        address: profile.address,
        mapa: profile.mapa_number,
      },
      clinic: { name: clinic?.name ?? profile.clinic_name ?? null, cnpj: null },
      medicalRecordId,
    };
  }, [profile, clinics, pet, medicalRecordId]);
}
